/*
<purpose>
  Which of a ticket's draft strips the clerk has DISCLOSED — expanded into their card. Sticky per
  TICKET for the browser session and shared by every window (operator ruling Q2 = B, 2026-09-17):
  what was expanded in the list is expanded in the pop-out, and the other way round.
</purpose>
<dependencies>sessionStorage (best-effort) · services/ticketSync (THE cross-window channel).</dependencies>
<constraints>
  - Keyed by TICKET, never by suggestion attempt: a reanalysis is a new letter but the same
    ticket, and the clerk's "show me the receipt" is a statement about the ticket. (The local
    `expandedCompanions` this replaces was attempt-keyed and window-local — it lost the
    disclosure on every remount, which was the defect.)
  - One MEANING of "expanded" for every strip kind: the resting companion strip writes it on
    press, and a lazy strip that COMPOSES writes it too. A surface asking "is this kind open"
    asks this module and gets the same answer whichever kind it holds.
  - Session-scoped by design. `window.open` hands the pop-out a COPY of sessionStorage at open
    time, so the copy alone would go stale the moment either window changed its mind — hence the
    announcement, which carries the PAYLOAD (uuid, kind, on) rather than a "go look" signal the
    other window could not act on.
  - sessionStorage may throw or be absent (private windows, blocked site data). Every access is
    guarded and an in-memory map stands in; the feature degrades to window-local, never to an
    exception on a clerk's screen.
  - ONE channel: services/ticketSync's. A parallel BroadcastChannel for this fact is the copy
    this module exists not to be.
</constraints>
*/

import type { DraftSlotKind } from './draftClaims';
import { announceDraftDisclosure, onDraftDisclosure } from './ticketSync';

const KEY_PREFIX = 'stx.drafts.disclosed.';
const keyOf = (uuid: string) => `${KEY_PREFIX}${uuid}`;

/** The stand-in store for a browser that refuses sessionStorage — window-local, never an error. */
const memory = new Map<string, ReadonlySet<DraftSlotKind>>();

const listeners = new Set<(uuid: string) => void>();

// Wired on first use rather than at import: a module that subscribes at import time opens the
// BroadcastChannel in every test that merely pulls the vocabulary in, and the feature detection
// in ticketSync is what decides whether there is a channel at all.
let wired = false;
function ensureWired(): void {
  if (wired) return;
  wired = true;
  onDraftDisclosure((uuid, kind, on) => {
    // A foreign window's sessionStorage is not ours — write the payload into THIS window's store
    // before telling anyone, so a listener that re-reads sees the announced state.
    writeLocal(uuid, kind as DraftSlotKind, on);
    listeners.forEach(cb => cb(uuid));
  });
}

function session(): Storage | null {
  try {
    const store = window.sessionStorage;
    const probe = `${KEY_PREFIX}probe`;
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

/** The disclosed kinds for one ticket, as the session last recorded them. Never throws. */
export function readDisclosed(uuid: string): ReadonlySet<DraftSlotKind> {
  ensureWired();
  const store = session();
  if (!store) return memory.get(uuid) ?? EMPTY;
  try {
    const raw = store.getItem(keyOf(uuid));
    if (raw === null) return EMPTY;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed as DraftSlotKind[]) : EMPTY;
  } catch {
    return memory.get(uuid) ?? EMPTY;
  }
}

const EMPTY: ReadonlySet<DraftSlotKind> = new Set();

function writeLocal(uuid: string, kind: DraftSlotKind, on: boolean): void {
  const next = new Set(readDisclosed(uuid));
  if (on) next.add(kind); else next.delete(kind);
  memory.set(uuid, next);
  const store = session();
  if (!store) return;
  try { store.setItem(keyOf(uuid), JSON.stringify(Array.from(next))); } catch { /* memory holds it */ }
}

/**
 * Record that this ticket's `kind` is disclosed (or is not any more), tell every surface in this
 * window, and tell the other windows. The one write path — a surface never touches the storage
 * key itself.
 */
export function setDisclosed(uuid: string, kind: DraftSlotKind, on: boolean): void {
  ensureWired();
  writeLocal(uuid, kind, on);
  listeners.forEach(cb => cb(uuid));
  // BroadcastChannel never echoes to the poster, which is why the local notify above is separate.
  announceDraftDisclosure(uuid, kind, on);
}

/** Hear every disclosure change — this window's own and the other windows'. Returns the unsubscribe. */
export function onDisclosureChanged(cb: (uuid: string) => void): () => void {
  ensureWired();
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
